/**
 * Environment Configuration and Validation
 *
 * This module validates that all required environment variables are set correctly
 * at application startup. It prevents the app from running in a broken state due
 * to missing configuration.
 */

// Define required variables and their descriptions
const REQUIRED_VARS = {
  VITE_BACKEND_URL: 'The base URL for the LLoan Shark backend API',
  VITE_WALLET_CONNECT_PROJECT_ID: 'Project ID for WalletConnect (Web3Modal)',
  VITE_ALCHEMY_KEY: 'API Key for Alchemy RPC provider',
};

// Define optional variables with warnings
const OPTIONAL_VARS = {
  VITE_GAME_API_URL: 'Separate URL for Game SDK endpoints (defaults to backend URL)',
  VITE_ENABLE_API_LOGS: 'Enable verbose API logging in production',
};

function validateEnv() {
  const missing = [];
  const warnings = [];

  // Check required variables
  for (const [key, desc] of Object.entries(REQUIRED_VARS)) {
    if (!import.meta.env[key]) {
      missing.push(`${key}: ${desc}`);
    }
  }

  // Check optional variables
  for (const [key, desc] of Object.entries(OPTIONAL_VARS)) {
    if (!import.meta.env[key]) {
      warnings.push(`${key}: ${desc}`);
    }
  }

  // Report issues
  if (missing.length > 0) {
    const errorMsg = `
CRITICAL CONFIGURATION ERROR:
The following environment variables are missing:

${missing.map(m => `- ${m}`).join('\n')}

Please check your .env file or deployment configuration.
    `.trim();

    // In development, stop execution with an alert (optional) or just log error
    console.error(errorMsg);
    
    // Create a visible error overlay if in development
    if (import.meta.env.DEV) {
      setTimeout(() => {
        // Test runners can tear down the DOM before this timer fires.
        if (typeof document === 'undefined') return;
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100vw';
        overlay.style.height = '100vh';
        overlay.style.backgroundColor = 'rgba(0,0,0,0.9)';
        overlay.style.color = '#ff4444';
        overlay.style.zIndex = '99999';
        overlay.style.padding = '2rem';
        overlay.style.fontFamily = 'monospace';
        overlay.style.whiteSpace = 'pre-wrap';
        overlay.innerText = errorMsg;
        document.body.appendChild(overlay);
      }, 100);
    }
  }

  if (warnings.length > 0 && import.meta.env.DEV) {
    console.warn('Environment Warnings:\n' + warnings.map(w => `- ${w}`).join('\n'));
  }

  return {
    backendUrl: import.meta.env.VITE_BACKEND_URL,
    gameApiUrl: import.meta.env.VITE_GAME_API_URL || import.meta.env.VITE_BACKEND_URL,
    walletConnectId: import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID,
    alchemyKey: import.meta.env.VITE_ALCHEMY_KEY,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD,
  };
}

// Export the validated config object
export const env = validateEnv();
